import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CASAS_MAX, CASAS_MIN, CASAS_PADRAO, exibicaoEmpresaSchema, type ExibicaoEmpresaDto } from '@triade/contracts';
import { useExibicao, useSalvarExibicao } from '@/api/exibicao';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { formatarArredondado } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';

type Campo = Exclude<keyof ExibicaoEmpresaDto, 'mostrarReformaFormacao'>;

const CAMPOS: Array<{ nome: Campo; rotulo: string; ajuda: string; exemplo: string; sufixo?: string; prefixo?: string }> = [
  { nome: 'casasDinheiro', rotulo: 'Dinheiro', ajuda: 'Preço, total, lucro em R$.', exemplo: '1234.56789012', prefixo: 'R$ ' },
  { nome: 'casasPercentual', rotulo: 'Percentual', ajuda: 'Lucro, margem, IPI, variações.', exemplo: '21.8456789', sufixo: '%' },
  { nome: 'casasMarkup', rotulo: 'Markup', ajuda: 'Fator do markup e divisor.', exemplo: '1.89412345678' },
  { nome: 'casasCusto', rotulo: 'Custo unitário e CPM', ajuda: 'Custo por unidade e custo por minuto.', exemplo: '0.27534567891', prefixo: 'R$ ' },
];

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/**
 * Empresa > Parametros > Exibicao: quantas casas decimais as telas, o PDF e as exportacoes mostram. SO apresentacao: o banco, o
 * motor e os calculos seguem na precisao propria (o Excel leva o valor completo e usa as casas so no formato da celula).
 * Sem vigencia: vale ao gravar, com trilha de auditoria. Financeiro e administradores editam; os demais so consultam.
 */
export function ExibicaoEmpresa({ editavel }: { editavel: boolean }) {
  const q = useExibicao();
  const salvar = useSalvarExibicao();
  const [texto, setTexto] = useState<Record<Campo, string>>({ casasDinheiro: '', casasPercentual: '', casasMarkup: '', casasCusto: '' });
  const [erro, setErro] = useState<string | null>(null);

  const gravada = q.data;
  useEffect(() => {
    if (gravada) {
      setTexto({
        casasDinheiro: String(gravada.casasDinheiro),
        casasPercentual: String(gravada.casasPercentual),
        casasMarkup: String(gravada.casasMarkup),
        casasCusto: String(gravada.casasCusto),
      });
    }
  }, [gravada]);

  const rascunho = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of CAMPOS) out[c.nome] = /^\d{1,2}$/.test(texto[c.nome].trim()) ? Number(texto[c.nome].trim()) : Number.NaN;
    return out;
  }, [texto]);
  const validacao = exibicaoEmpresaSchema.safeParse(rascunho);
  const erroDe = (nome: Campo): string | undefined => (validacao.success ? undefined : validacao.error.issues.find((i) => i.path[0] === nome)?.message);
  const alterado = !!gravada && CAMPOS.some((c) => texto[c.nome].trim() !== String(gravada[c.nome]));

  async function gravar() {
    if (!validacao.success) return;
    setErro(null);
    try {
      await salvar.mutateAsync(validacao.data);
      // Toast (nao texto na secao): ao gravar, as casas mudam e a arvore remonta, entao o estado local nao sobrevive.
      toast.success('Casas de exibição aplicadas. Telas, PDF e exportações já usam as novas casas.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  const casas = (n: Campo): number => (validacao.success ? validacao.data[n] : (gravada?.[n] ?? CASAS_PADRAO[n]));

  return (
    <section aria-label="Exibição de números" data-testid="secao-exibicao" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Exibição de números</h2>
        <p className="mt-1 text-small text-ink-secondary">
          Quantas casas decimais aparecem nas telas, no PDF e nas exportações ({CASAS_MIN} a {CASAS_MAX}). Só muda a apresentação: o que é gravado e o que é
          calculado seguem na precisão de sempre, e o Excel leva o valor completo na célula. Vale na hora para a empresa toda.
        </p>
      </div>
      {q.isError && <ErroFormacao erro={q.error} onTentar={() => void q.refetch()} titulo="Não foi possível carregar as casas" />}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-4">
        {CAMPOS.map((c) => {
          const e = erroDe(c.nome);
          const exemplo = formatarArredondado(c.exemplo, casas(c.nome));
          return (
            <div key={c.nome}>
              <Label htmlFor={`exi-${c.nome}`} className="mb-1.5 block text-caption font-medium text-ink-secondary">
                {c.rotulo}
              </Label>
              <Input
                id={`exi-${c.nome}`}
                inputMode="numeric"
                disabled={!editavel || !gravada}
                value={texto[c.nome]}
                onChange={(ev) => setTexto((t) => ({ ...t, [c.nome]: ev.target.value }))}
                aria-invalid={e ? true : undefined}
                aria-describedby={`exi-${c.nome}-nota`}
                className="w-24"
              />
              <p id={`exi-${c.nome}-nota`} className={cn('mt-1 text-caption', e ? 'text-danger-ink' : 'text-ink-muted')}>
                {e ?? c.ajuda}
              </p>
              {!e && (
                <p className="tabular mt-0.5 text-caption text-ink-secondary" data-testid={`exemplo-${c.nome}`}>
                  Exemplo: {c.prefixo ?? ''}
                  {exemplo}
                  {c.sufixo ?? ''}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {!editavel ? (
        <p role="note" data-testid="aviso-exibicao-somente-leitura" className="text-small text-ink-muted">
          Seu papel só consulta: financeiro e administradores alteram as casas.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={() => void gravar()} disabled={!alterado || !validacao.success || salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Aplicar casas'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={salvar.isPending}
            onClick={() => setTexto({ casasDinheiro: String(CASAS_PADRAO.casasDinheiro), casasPercentual: String(CASAS_PADRAO.casasPercentual), casasMarkup: String(CASAS_PADRAO.casasMarkup), casasCusto: String(CASAS_PADRAO.casasCusto) })}
          >
            Restaurar padrões
          </Button>
          {erro && <span role="alert" className="text-small text-danger-ink">{erro}</span>}
        </div>
      )}
    </section>
  );
}

/**
 * Empresa > Parametros: mostra ou esconde o bloco "Reforma tributária" na Formação de preço (Sessão 36). Só apresentação; padrão ligado.
 * Grava na hora (PUT /empresa/exibicao com as casas já gravadas). Financeiro e administradores editam; os demais só consultam.
 */
export function ReformaFormacaoEmpresa({ editavel }: { editavel: boolean }) {
  const q = useExibicao();
  const salvar = useSalvarExibicao();
  const [erro, setErro] = useState<string | null>(null);
  const g = q.data;
  const ligado = g?.mostrarReformaFormacao ?? true;

  async function alternar(valor: boolean) {
    if (!g) return;
    setErro(null);
    try {
      await salvar.mutateAsync({
        casasDinheiro: g.casasDinheiro,
        casasPercentual: g.casasPercentual,
        casasMarkup: g.casasMarkup,
        casasCusto: g.casasCusto,
        mostrarReformaFormacao: valor,
      });
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <section aria-label="Reforma tributária na Formação" data-testid="secao-reforma-formacao" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Reforma tributária na Formação</h2>
        <p className="mt-1 text-small text-ink-secondary">
          Mostra o bloco opcional “Reforma tributária” (CBS/IBS e preço por regime) na tela Formação de preço. Só muda a apresentação: os cálculos e o motor não mudam.
        </p>
      </div>
      <label className="flex items-center gap-2.5 text-small">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={ligado}
          disabled={!editavel || !g || salvar.isPending}
          onChange={(e) => void alternar(e.target.checked)}
        />
        Mostrar a Reforma tributária na Formação de preço
      </label>
      {!editavel && <p className="text-caption text-ink-muted">Seu papel só consulta: financeiro e administradores alteram.</p>}
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}
    </section>
  );
}
